import React, { useEffect, useRef, useState } from "react";
import "./index.scss";
import Header from "../../../components/layout/header";
import { Formik, Form } from "formik";
import * as PR from "../../../prime-modules/index";
import { User, Password, Mail, data2goSIM, iccidIcon } from "../../../assets/images";
import { useMutation } from "react-query";
import { handleSignup } from "../../../services/api";
import { signUpValidationSchema } from "../../../utils/schema";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../components/form/Input";
import { useToast } from "../../../context/ToastContext";
import GoogleAndAppleLoginButtons from "../../../components/auth/GoogleAndAppleLoginButtons";
import { useTranslationContext } from "../../../context/TranslationContext";
import {  useLocation, useNavigate } from "react-router-dom";
import Footer from "../../../components/layout/footer";
import LandingPage from "../landing-page/LandingPage";
import { login } from "../../../store/auth.slice";

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { showToast } = useToast()
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [iccid, setIccid] = useState(!!state?.iccid ? state?.iccid : '');
  const [iccidPIN, setIccidPIN] = useState(!!state?.pin ? state?.pin : '');
  const [iccidError, setIccidError] = useState("")
  const [iccidErrorPIN, setIccidErrorPIN] = useState("")
  const { labelsIot, validationsIot } = useTranslationContext()
  const formikRef = useRef(null);

  useEffect(() => {
    if(isAuthenticated){
      navigate("/dashboard")
    }
  }, [isAuthenticated, navigate])

  const { mutate, isLoading } = useMutation(handleSignup, {
    onSuccess: (o) => {
      showToast("success", "SIGNUP-07")
      if(o?.sessionId) {
        dispatch(login(o));
      } else {
        setIccidError("");
        formikRef.current.resetForm();
        navigate(`/verify-email`);
      }
    },
    onError: (o) => showToast("error", o?.message)
  })

  const initialValues = {
    firstName: "",
    email: "",
    password: "",
    iccidPin:""
  }

  const handleOnBlur = ()=>{
    if(!iccid){
      setIccidError("VLDN0037")
      return false
    }
    if(iccid.length < 18){
      setIccidError("VLDN0045")
      return false
    }
    setIccidError("");
    return true
  }

  const handleOnBlurPIN = ()=>{
    if(!iccidPIN){
      setIccidErrorPIN("VLDN0044")
      return false
    }
    if(iccidPIN.length < 4){
      setIccidErrorPIN("VLDN0045")
      return false
    }
    setIccidErrorPIN("");
    return true
  }

  const iccidCheck=()=>{
    return handleOnBlur() && handleOnBlurPIN()
  }

  return (
    <>
      <section className="auth-wrapper">
        {isLoading && <div className="loading-section loading-block">{labelsIot?.LBLI0139}</div>}
        <Header iccid={iccid} />
        <div className="form-wrapper">
          <div className="grid grid-nogutter align-items-center w-12">
            <div className="col-12 md:col-6 left-block landing-page-block">
              <PR.Image src={data2goSIM} alt="ICCID" className="sim-image" />
              <h1>{labelsIot?.LBLI0211 || "You're just one step away"} <br/> {labelsIot?.LBLI0212 || "from getting connected."}</h1>
              <p>{labelsIot?.LBLI0213 || "Activate your SIM and enjoy fast, reliable IoT connectivity - no hassle, no confusion. Plug-in, Power up, and you're good to go."}</p>
            </div>
            <div className="col-12 md:col-6">
              <div className="form-block">
                <Formik innerRef={formikRef} initialValues={initialValues}
                  validationSchema={signUpValidationSchema}
                  onSubmit={(values, { setSubmitting }) => {
                    if(handleOnBlur() && handleOnBlurPIN()) {
                     values.iccid = iccid?.toString();
                     values.iccidPin = iccidPIN?.toString()
                    mutate(values);
                    setSubmitting(false);
                   }
                  }}>
                  <Form autoComplete="off">
                    <div className="card flex flex-column relative">
                      <div className="grid">
                        <div className="col-12">
                            <div className="flex flex-column gap-2">
                              <PR.InputText
                                invalid={!!iccidError}
                                value={iccid}
                                placeholder={labelsIot?.LBLI0035}
                                maxLength={22}
                                onBlur={handleOnBlur}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  // Remove leading spaces
                                  value = value.replace(/^\s+/, '');
                                  const isValid = /^[0-9]+(\s[0-9]+)*$/.test(value);
                                  if (isValid || value === '') {
                                    setIccid(value);
                                  }
                                  setIccidError("")
                                }}
                                className={`w-12 ${iccidError || iccidErrorPIN ? 'invalid-class' : 'valid-class'}`}
                                useGrouping={false}
                              />
                              <PR.InputText
                                invalid={!!iccidErrorPIN}
                                placeholder="****"
                                className="pin-input border-1 valid-class"
                                style={{ border: "none",borderLeft: `1px solid ${iccidErrorPIN ? '#F73859' : "#BCBCFF"}` ,borderRadius:0 }}
                                minLength={4}
                                maxLength={4}
                                value={iccidPIN}
                                onBlur={handleOnBlurPIN}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  // Remove leading spaces
                                  value = value.replace(/^\s+/, '');
                                  // Allow only numbers and single spaces between numbers
                                  const isValid = /^[0-9]+(\s[0-9]+)*$/.test(value);
                                  if (isValid || value === '') {
                                    setIccidPIN(value);
                                  }
                                  setIccidErrorPIN("")
                                }}
                              />
                            </div>
                          <PR.Image src={iccidIcon} alt="ICCID" className="icon" />
                        </div>
                        <span className="err-msg ml-2 mt-0">{validationsIot ? validationsIot[iccidError] || validationsIot[iccidErrorPIN] || '' : null}</span>
                      </div>
                    </div>
                    <div className="card flex flex-column relative">
                      <div className="grid">
                        <div className="col-12">
                          <Input
                          keyfilter={/^[a-zA-Z0-9\s]*$/}
                            label={""}
                            name={"firstName"}
                            type={"text"}
                            placeholder={labelsIot?.LBLI0122}
                          />
                          <PR.Image src={User} alt="Mail" className="icon" />
                        </div>
                      </div>
                    </div>
                    <div className="card flex flex-column relative mb-2">
                      <Input
                        label={""}
                        name={"email"}
                        type={"email"}
                        placeholder={labelsIot?.LBLI0010}
                      />
                      <PR.Image src={Mail} alt="Mail" className="icon" />
                    </div>
                    <div className="card flex flex-column relative gap-2">
                      <Input
                        label={""}
                        name={"password"}
                        type={"password"}
                        placeholder={labelsIot?.LBLI0009}
                        feedback={false}
                        toggleMask
                      />
                      <PR.Image src={Password} alt="Mail" className="icon" />
                    </div>
                    <div className="card flex flex-column">
                      <PR.Button disabled={isLoading} label={labelsIot?.LBLI0003} className="login-button" type="submit" />
                    </div>
                    <GoogleAndAppleLoginButtons iccidPin={iccidPIN} iccid={iccid} iccidCheck={iccidCheck} />
                  </Form>
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </section>
      <LandingPage/>
      <Footer/>
    </>
  );
};

export default SignUp;